// CONTRACT:
// PURPOSE: [Company card in rating list with expandable index breakdown toggle]
// SCOPE: [rating list, card, expandable, index-breakdown]
// INPUT: [company object, rank number]
// OUTPUT: [styled card with meta-info, trust badge, and expandable breakdown section]
// KEYWORDS: [DOMAIN(SEO): rating-card, company-card; CONCEPT(ui): expandable, collapse; UI: card, meta, badge, toggle]

"use client";

import { useState } from "react";
import Link from "next/link";
import {
  MapPin,
  Hash,
  Calendar,
  Shield,
  Star,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  User,
} from "lucide-react";
import {
  getTrustBadge,
  getTopRankStyle,
  getAge,
} from "@oxpa/shared/lib/trust-utils";
import { CompanyIndexBreakdown } from "./company-index-breakdown";
import type { Json } from "@/types/database";

// # START_INTERFACE_RatingCompanyCardProps
interface RatingCompanyCardProps {
  company: {
    id: string;
    name_short: string;
    inn: string | null;
    ogrn: string | null;
    director_name: string | null;
    status: string | null;
    region: string | null;
    trust_score: number | null;
    trust_index_v54?: number | null;
    trust_index_v54_without_age?: number | null;
    trust_breakdown_v54?: Json | null;
    rating: number | null;
    reviews_count: number | null;
    registration_date: string | null;
    slug: string | null;
    address: string | null;
  };
  rank: number;
}
// # END_INTERFACE_RatingCompanyCardProps

// # START_FUNCTION_RatingCompanyCard
/**
 * Карточка компании в списке рейтинга с:
 * - Ранг, название, мета-информация (регион, ИНН, директор, возраст, статус)
 * - Trust Score Badge
 * - Expandable toggle для подробной разбивки индекса
 *
 * Корневой элемент — <div> (не Link), чтобы кнопка expandable не спавнила переход.
 * Ранг и название — отдельные <Link> для навигации.
 */
export function RatingCompanyCard({ company, rank }: RatingCompanyCardProps) {
  const [open, setOpen] = useState(false);

  // MEMENTO: v5.4 — новый движок (trust_index_v54); fallback на старый trust_score,
  // пока прод не мигрирован (v54 = NULL). nullsFirst:false в сортировке списка.
  const score = company.trust_index_v54 ?? company.trust_score ?? 0;
  const badge = getTrustBadge(score);
  const isLiquidated = company.status !== "ACTIVE";
  const topStyle = getTopRankStyle(rank);
  const href = company.slug
    ? `/chop/${company.slug}`
    : company.inn
      ? `/chop/${company.inn}`
      : "#";

  return (
    <div
      className={`block bg-[hsl(var(--portal-bg))] border-b border-[hsl(var(--portal-border-hover))] p-4 md:p-5 transition-all ${topStyle} ${isLiquidated ? "opacity-50" : ""}`}
    >
      <div className="flex items-start gap-3 md:gap-6">
        {/* Ранг — Link */}
        <Link
          href={href}
          className="flex-shrink-0 w-8 md:w-12 text-center hover:opacity-70 transition-opacity"
        >
          <span
            className={`text-lg md:text-2xl font-black font-mono tabular-nums ${
              rank <= 3 ? "text-[hsl(var(--portal-warn))]" : "text-[hsl(var(--portal-text-dim))]"
            }`}
          >
            {String(rank).padStart(2, "0")}
          </span>
        </Link>

        {/* Основная информация */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 md:gap-3 mb-2">
            <div className="min-w-0">
              {/* Название компании — Link */}
              <Link
                href={href}
                className="hover:opacity-80 transition-opacity"
              >
                <h2 className="text-sm md:text-base font-bold uppercase text-[hsl(var(--portal-text))] break-words">
                  {company.name_short}
                </h2>
              </Link>
              {/* meta: region, inn */}
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {company.region && (
                  <span className="text-[11px] text-[hsl(var(--portal-text-muted))] font-sans flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {company.region}
                  </span>
                )}
                {company.inn && (
                  <>
                    <span className="text-[11px] text-[hsl(var(--portal-text-dim))]">•</span>
                    <span className="text-[11px] text-[hsl(var(--portal-text-muted))] font-mono tabular-nums flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      ИНН {company.inn}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Trust Score Badge */}
            <div className="flex items-center gap-2 shrink-0">
              <div
                className={`flex items-center gap-1.5 border px-2.5 py-1 md:px-3 md:py-1.5 rounded-none ${badge.color}`}
              >
                <TrendingUp className="w-3 h-3 md:w-3.5 md:h-3.5" />
                <span className="text-xs md:text-sm font-black font-mono tabular-nums">
                  {score.toFixed(0)}
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-[hsl(var(--portal-text-dim))] hidden md:block" />
            </div>
          </div>

          {/* Мета-данные */}
          <div className="flex flex-wrap gap-3 md:gap-5">
            {company.director_name && (
              <div className="flex items-center gap-1.5 hidden md:flex">
                <User className="w-3 h-3 shrink-0 text-[hsl(var(--portal-text-dim))]" />
                <span className="text-[11px] text-[hsl(var(--portal-text-muted))] font-sans">
                  {company.director_name}
                </span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3 h-3 text-[hsl(var(--portal-text-dim))]" />
              <span className="text-[11px] text-[hsl(var(--portal-text-muted))] font-mono tabular-nums">
                {getAge(company.registration_date)}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Shield className="w-3 h-3 text-[hsl(var(--portal-text-dim))]" />
              <span
                className={`text-[11px] font-sans ${
                  company.status === "ACTIVE" ? "text-[hsl(var(--portal-accent))]" : "text-[hsl(var(--portal-danger))]"
                }`}
              >
                {company.status === "ACTIVE" ? "Действует" : "Ликвидировано"}
              </span>
            </div>
            {(company.reviews_count || 0) > 0 && (
              <div className="flex items-center gap-1.5">
                <Star className="w-3 h-3 text-[hsl(var(--portal-warn))]" />
                <span className="text-[11px] text-[hsl(var(--portal-text-muted))] font-mono tabular-nums">
                  {company.rating?.toFixed(1)} ({company.reviews_count})
                </span>
              </div>
            )}
          </div>

          {/* Кнопка expandable breakdown */}
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-accent-hover))] font-mono uppercase tracking-wider transition-colors"
          >
            <span>Из чего сложился индекс</span>
            <ChevronDown
              className={`w-3 h-3 text-[hsl(var(--portal-accent))] transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Expandable breakdown section */}
      {open && (
        <div className="mt-4 pt-4 border-t border-[hsl(var(--portal-border))]">
          <CompanyIndexBreakdown
            v54Breakdown={company.trust_breakdown_v54}
            rating={company.rating}
            reviewsCount={company.reviews_count}
            status={company.status}
            registrationDate={company.registration_date}
            inn={company.inn}
            ogrn={company.ogrn}
            directorName={company.director_name}
            address={company.address}
          />
        </div>
      )}
    </div>
  );
}
// # END_FUNCTION_RatingCompanyCard
